import { ApiErrorSchema, type ApiErrorCode } from "@station-keeping/contracts";

/** Réponse d'erreur de l'API : un statut HTTP et un code métier stable (voir `API_ERROR_CODES`). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode | (string & {}),
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Erreur de l'API à partir du corps reçu (format unique `ApiErrorSchema`), ou d'un statut seul. */
  static from(status: number, body: unknown): ApiError {
    const parsed = ApiErrorSchema.safeParse(body);
    if (parsed.success) {
      const { code, message, details } = parsed.data.error;
      return new ApiError(status, code, message, details);
    }
    return new ApiError(status, status >= 500 ? "INTERNAL_ERROR" : "UNKNOWN", `Erreur HTTP ${status}`);
  }
}

/** Pas de réponse : réseau coupé, serveur injoignable ou trop lent. */
export class NetworkError extends Error {
  constructor(message = "Serveur injoignable") {
    super(message);
    this.name = "NetworkError";
  }
}

export function isApiError(error: unknown, code?: ApiErrorCode): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

/** Vaut-il la peine de renvoyer la même requête plus tard ? (coupure, serveur surchargé ou en panne passagère) */
export function isTransient(error: unknown): boolean {
  if (error instanceof NetworkError) return true;
  if (!(error instanceof ApiError)) return false;
  if (error.status === 429) return true;
  return error.status >= 500 && error.code !== "STATION_UNAVAILABLE" && error.code !== "EMAIL_LOGIN_CLOSED";
}
